import { useState, useEffect } from 'react';
import axios from 'axios';

const CreateSection = () => {
  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState('');
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedSection, setSelectedSection] = useState('');
  const [sectionRows, setSectionRows] = useState([]);
  const [showTable, setShowTable] = useState(false);
  const [existingSections, setExistingSections] = useState([]);

  // Load all sessions
  useEffect(() => {
    const fetchSessions = async () => {
      try {
        const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/sessions/allSessions`);
        setSessions(res.data);
      } catch (err) {
        console.error('Error fetching sessions:', err);
      }
    };
    fetchSessions();
  }, []);

  // Load classes when session changes
  useEffect(() => {
    if (!selectedSession) {
      setClasses([]);
      setSelectedClass('');
      return;
    }

    const fetchClasses = async () => {
      try {
        const res = await axios.get(` ${process.env.REACT_APP_API_BASE_URL}/api/classes/getClasses?sessionId=${selectedSession}`);
        setClasses(res.data);
      } catch (err) {
        console.error('Error fetching classes:', err);
        setClasses([]);
      }
    };

    fetchClasses();
    setSelectedClass('');
    setExistingSections([]);
  }, [selectedSession]);

  // Load existing sections when class or session changes
  useEffect(() => {
    if (!selectedClass || !selectedSession) {
      setExistingSections([]);
      return;
    }

    const fetchSections = async () => {
      try {
        const selectedClassName = classes.find(c => c._id === selectedClass)?.className;
        const res = await axios.get(
          ` ${process.env.REACT_APP_API_BASE_URL}/api/sections/${encodeURIComponent(selectedClassName)}/getSectionsByClassName?sessionId=${selectedSession}`
        );
        if (Array.isArray(res.data)) setExistingSections(res.data);
        else if (res.data.sections) setExistingSections(res.data.sections);
        else setExistingSections([]);
      } catch (err) {
        console.error('Error fetching sections:', err);
        setExistingSections([]);
      }
    };

    fetchSections();
  }, [selectedClass, selectedSession, classes]);

  // Generate new section rows
  useEffect(() => {
    if (selectedSection) {
      setSectionRows(
        Array.from({ length: Number(selectedSection) }, (_, i) => ({
          id: i + 1,
          name: '',
          medium: 'English',
        }))
      );
    } else {
      setSectionRows([]);
      setShowTable(false);
    }
  }, [selectedSection]);

  const handleGenerateSections = () => {
    if (selectedClass && selectedSection) {
      setShowTable(true);
    } else {
      alert("Please select both class and number of sections.");
    }
  };

  const handleSaveSections = async () => {
    if (!selectedClass || sectionRows.some(row => !row.name || !row.medium)) {
      alert("Please fill out all fields");
      return;
    }

    try {
      const sectionsData = sectionRows.map(row => ({
        sectionName: row.name,
        medium: row.medium,
      }));

      const selectedClassName = classes.find(c => c._id === selectedClass)?.className;

      await axios.post(
        ` ${process.env.REACT_APP_API_BASE_URL}/api/sections/${selectedClassName}/createSections`,
        sectionsData,
        { params: { sessionId: selectedSession } } // send sessionId
      );

      setSelectedSection('');
      setSectionRows([]);
      setShowTable(false);

      // Refresh the existing sections list
      const res = await axios.get(
        ` ${process.env.REACT_APP_API_BASE_URL}/api/sections/${encodeURIComponent(selectedClassName)}/getSectionsByClassName?sessionId=${selectedSession}`
      );
      if (Array.isArray(res.data)) setExistingSections(res.data);
      else if (res.data.sections) setExistingSections(res.data.sections);
      else setExistingSections([]);
    } catch (err) {
      console.error('Error creating sections:', err);
    }
  };

  const labelStyle = "block text-black font-semibold text-[18px] mb-2";
  const inputStyle =
    "w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm bg-white text-gray-800 text-[14px] placeholder:text-[14px] placeholder:text-gray-400 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500";
const handleDeleteSection = async (sectionName) => {
  if (!window.confirm("Are you sure you want to delete this section?")) return;

  try {
    const selectedClassName = classes.find(c => c._id === selectedClass)?.className;

    await axios.delete(
      ` ${process.env.REACT_APP_API_BASE_URL}/api/sections/${encodeURIComponent(selectedClassName)}/deleteSectionByName/${encodeURIComponent(sectionName)}`,
      {
        params: { sessionId: selectedSession }
      }
    );

    // refresh list after delete
    const res = await axios.get(
      ` ${process.env.REACT_APP_API_BASE_URL}/api/sections/${encodeURIComponent(selectedClassName)}/getSectionsByClassName?sessionId=${selectedSession}`
    );

    if (Array.isArray(res.data)) setExistingSections(res.data);
    else if (res.data.sections) setExistingSections(res.data.sections);
    else setExistingSections([]);

  } catch (err) {
    console.error("Error deleting section:", err);
    alert("Failed to delete section");
  }
};


  return (
    <div className="p-6 bg-white shadow-md rounded-lg w-full pl-6">
      {/* Session selection */}
      <div className="flex flex-col md:flex-row gap-4 mb-4">
        <div className="flex-1">
          <label className={labelStyle}>Select Session</label>
          <select
            value={selectedSession}
            onChange={(e) => setSelectedSession(e.target.value)}
            className={inputStyle}
          >
            <option value="">Select Session</option>
            {sessions.map(s => (
              <option key={s._id} value={s._id}>{s.sessionName}</option>
            ))}
          </select>
        </div>

        <div className="flex-1">
          <label className={labelStyle}>Select Class</label>
          <select
            value={selectedClass}
            onChange={(e) => setSelectedClass(e.target.value)}
            className={inputStyle}
            disabled={!selectedSession}
          >
            <option value="">Select Class</option>
            {classes.map(c => (
              <option key={c._id} value={c._id}>{c.className}</option>
            ))}
          </select>
        </div>

        <div className="flex-1">
          <label className={labelStyle}>Number of Sections</label>
          <select
            value={selectedSection}
            onChange={(e) => setSelectedSection(e.target.value)}
            className={inputStyle}
            disabled={!selectedClass}
          >
            <option value="">Select Number of Sections</option>
            {[1, 2, 3, 4, 5].map(n => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
        </div>
      </div>

      <button
        onClick={handleGenerateSections}
        className="px-6 py-2 bg-blue-500 text-white font-semibold rounded-md mb-4"
      >
        Generate Sections
      </button>

      {/* New section creation table */}
      {showTable && sectionRows.length > 0 && (
        <div className="mb-6">
          <h3 className="font-semibold text-black text-[18px] mb-4">Section Details</h3>
          <table className="min-w-[600px] bg-white border border-gray-300 rounded-lg">
            <thead className="bg-gray-200">
              <tr>
                <th className="py-2 px-4 border-b font-semibold text-black text-[18px]">Class Name</th>
                <th className="py-2 px-4 border-b font-semibold text-black text-[18px]">Section Name</th>
                <th className="py-2 px-4 border-b font-semibold text-black text-[18px]">Medium</th>
              </tr>
            </thead>
            <tbody>
              {sectionRows.map(row => (
                <tr key={row.id}>
                  <td className="py-2 px-4 border-b">{classes.find(c => c._id === selectedClass)?.className}</td>
                  <td className="py-2 px-4 border-b">
                    <input
                      type="text"
                      value={row.name}
                      onChange={(e) => {
                        const newRows = [...sectionRows];
                        newRows[row.id - 1].name = e.target.value;
                        setSectionRows(newRows);
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-indigo-500 text-[14px]"
                      placeholder="Enter Section Name"
                    />
                  </td>
                  <td className="py-2 px-4 border-b">
                    <select
                      value={row.medium}
                      onChange={(e) => {
                        const newRows = [...sectionRows];
                        newRows[row.id - 1].medium = e.target.value;
                        setSectionRows(newRows);
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-indigo-500 text-[14px]"
                    >
                      <option value="English">English</option>
                      <option value="Hindi">Hindi</option>
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <button
            onClick={handleSaveSections}
            className="px-6 py-2 bg-blue-800 text-white font-semibold rounded-md mt-4"
          >
            Save Sections
          </button>
        </div>
      )}

      {/* Existing sections list */}
      {existingSections.length > 0 && (
       <div className="mt-6">
  <h3 className="font-semibold text-black text-[18px] mb-2">Existing Sections</h3>

  <div className="space-y-2">
    {existingSections.map(sec => (
      <div
        key={sec.sectionName}
        className="flex items-center justify-between bg-gray-100 px-4 py-2 rounded-md"
      >
        <span>
          {sec.sectionName} ({sec.medium})
        </span>

        <button
          onClick={() => handleDeleteSection(sec.sectionName)}
          className="px-3 py-1 bg-red-500 text-white text-sm rounded-md hover:bg-red-700"
        >
          Delete
        </button>
      </div>
    ))}
  </div>
</div>

      )}
    </div>
  );
};

export default CreateSection;
